import {
  Component,
  OnInit,
  Inject,
  Output,
  EventEmitter,
  HostListener,
  OnDestroy
} from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { CookieService } from 'ngx-cookie-service';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';

import { LanguageService } from '../../../core/services/language.service';
import { environment } from '../../../../environments/environment';
import { AuthenticationService } from '../../../core/services/authentication.service';
import { UserProfileService } from 'src/app/core/services/user.service';

import { NotificationService } from 'src/app/core/services/notification.service';
import { AppNotification } from 'src/app/core/models/notification';

import { ChatService } from 'src/app/core/services/chat.service';
import { ChatThread } from 'src/app/core/models/message';

@Component({
  selector: 'app-topbar',
  templateUrl: './topbar.component.html',
  styleUrls: ['./topbar.component.scss']
})
export class TopbarComponent implements OnInit, OnDestroy {

  user: any;
  element: any;
  configData: any;
  cookieValue: any;
  flagvalue: any;
  countryName: any;
  valueset!: string;

  @Output() mobileMenuButtonClicked = new EventEmitter();
  @Output() settingsButtonClicked = new EventEmitter();

  private subs = new Subscription();

  // =========================
  // Notifications
  // =========================
  notifications: AppNotification[] = [];
  unreadCount = 0;
  notifPage = 1;
  notifTotalPages = 1;
  notifLoading = false;

  private notifAudio: HTMLAudioElement | null = null;
  private notifListInitialized = false;
  private lastSeenNotifId: string | null = null;

  private hasUserInteracted = false;
  private soundUnlocked = false;

  // =========================
  // Chat preview state
  // =========================
  chatPreviewLoading = false;
  chatPreviewThreads: ChatThread[] = [];
  chatUnreadCount = 0;
  isStaffUser = false;
  isPatientUser = false;
  private messageAudio: HTMLAudioElement | null = null;

  listLang = [
    { text: 'English', flag: 'assets/images/flags/us.jpg', lang: 'en' },
    { text: 'Spanish', flag: 'assets/images/flags/spain.jpg', lang: 'es' },
    { text: 'German', flag: 'assets/images/flags/germany.jpg', lang: 'de' },
    { text: 'Italian', flag: 'assets/images/flags/italy.jpg', lang: 'it' },
    { text: 'Russian', flag: 'assets/images/flags/russia.jpg', lang: 'ru' },
  ];

  constructor(
    @Inject(DOCUMENT) private document: any,
    private router: Router,
    public languageService: LanguageService,
    public cookiesService: CookieService,
    private userService: UserProfileService,
    private authenticationService: AuthenticationService,
    private notificationService: NotificationService,
    private chatService: ChatService
  ) { }

  ngOnInit(): void {
    this.userService.getUserProfile().subscribe({
      next: (user) => {
        this.user = user;
      },
      error: (err) => {
        console.error('Error fetching profile:', err);
      }
    });

    this.element = document.documentElement;
    this.configData = {
      suppressScrollX: true,
      wheelSpeed: 0.3
    };

    this.cookieValue = this.cookiesService.get('lang');
    const val = this.listLang.filter(x => x.lang === this.cookieValue);
    this.countryName = val.map(element => element.text);

    if (val.length === 0) {
      if (this.flagvalue === undefined) {
        this.valueset = 'assets/images/flags/us.jpg';
      }
    } else {
      this.flagvalue = val.map(element => element.flag);
    }

    // role flags
    this.isStaffUser = this.chatService.isStaff();
    this.isPatientUser = this.chatService.isPatient();

    // bootstrap
    this.initNotifications();
    this.initNotificationSound();
    this.initMessageSound();
    this.initChatPreview();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  // =========================
  // Chat preview helpers
  // =========================
  private initChatPreview(): void {
    this.loadChatPreview();

    this.subs.add(
      this.chatService.onNewMessage().subscribe((evt: any) => {
        if (!evt?.threadId || !evt?.message) return;

        // sirf dusray user ke message par sound chale
        if (!this.isMyIncomingSocketMessage(evt.message)) {
          this.playMessageSound();
        }

        this.loadChatPreview();
      })
    );
  }

  private isMyIncomingSocketMessage(message: any): boolean {
    const myId = String(this.chatService.getCurrentUserId() || '');
    const senderId = this.extractSenderUserId(message);

    if (myId && senderId) {
      return myId === senderId;
    }

    // fallback by role
    const senderRole = String(message?.senderRole || '').toUpperCase();

    if (this.isPatientUser) {
      return senderRole === 'PATIENT';
    }

    return senderRole !== 'PATIENT';
  }

  private extractSenderUserId(message: any): string {
    const raw = message?.senderUserId;

    if (!raw) return '';
    if (typeof raw === 'string') return raw;
    if (typeof raw === 'object' && raw._id) return String(raw._id);
    if (typeof raw === 'object' && raw.id) return String(raw.id);

    return String(raw);
  }

  loadChatPreview(): void {
    this.chatPreviewLoading = true;

    const params: any = {
      page: 1,
      limit: 4
    };

    // staff ke liye open threads hi enough hote hain
    if (this.isStaffUser) {
      params.status = 'OPEN';
    }

    this.chatService.listThreads(params).subscribe({
      next: (data) => {
        const list = (data?.threads || [])
          .slice()
          .sort((a, b) => {
            const aTime = new Date(a.updatedAt || a.createdAt).getTime();
            const bTime = new Date(b.updatedAt || b.createdAt).getTime();
            return bTime - aTime;
          })
          .slice(0, 4);

        this.chatPreviewThreads = list;
        this.chatUnreadCount = list.reduce((sum, t) => {
          return sum + this.getUnreadForCurrentUser(t);
        }, 0);

        this.chatPreviewLoading = false;
      },
      error: (err) => {
        console.error('Chat preview load error', err);
        this.chatPreviewThreads = [];
        this.chatUnreadCount = 0;
        this.chatPreviewLoading = false;
      }
    });
  }

  getUnreadForCurrentUser(t: ChatThread): number {
    if (this.isPatientUser) {
      return Number(t?.unreadForPatient || 0);
    }
    return Number(t?.unreadForAdmin || 0);
  }

  getChatPreviewTitle(t: ChatThread): string {
    if (this.isStaffUser) {
      const patient: any =
        typeof t.patientId === 'object' && t.patientId ? t.patientId : null;

      return patient?.fullName || 'Patient';
    }

    // patient portal side
    const lastSenderRole = String(t?.lastMessage?.senderRole || '').toUpperCase();

    if (lastSenderRole && lastSenderRole !== 'PATIENT') {
      return 'Clinic Staff';
    }

    return 'Clinic';
  }

  getChatPreviewAvatar(t: ChatThread): string {
    return this.getInitials(this.getChatPreviewTitle(t));
  }

  getChatPreviewText(t: ChatThread): string {
    const text = String(t?.lastMessage?.text || '').trim();
    return text || 'No messages yet';
  }

  getChatPreviewTime(t: ChatThread): string {
    const date = t?.lastMessage?.sentAt || t?.updatedAt || t?.createdAt;
    return this.formatRelativeTime(date);
  }

  openChatFromTopbar(t?: ChatThread): void {
    this.router.navigate(['/chat']);
  }

  private getInitials(name?: string | null): string {
    if (!name) return '?';
    const parts = name.trim().split(/\s+/);
    const first = parts[0]?.charAt(0) || '';
    const last = parts.length > 1 ? parts[parts.length - 1].charAt(0) : '';
    return (first + last).toUpperCase();
  }

  formatRelativeTime(date: string | Date | null | undefined): string {
    if (!date) return '';
    const d = new Date(date);
    if (Number.isNaN(d.getTime())) return '';

    const diffMs = Date.now() - d.getTime();
    const sec = Math.floor(diffMs / 1000);
    const min = Math.floor(sec / 60);
    const hr = Math.floor(min / 60);
    const day = Math.floor(hr / 24);

    if (sec < 60) return 'now';
    if (min < 60) return `${min}m`;
    if (hr < 24) return `${hr}h`;
    if (day < 7) return `${day}d`;

    return d.toLocaleDateString();
  }

  // =========================
  // Notification helpers
  // =========================
  private initNotifications() {
    this.notificationService.recent$.subscribe(list => {
      const previousIds = new Set(this.notifications.map(n => n._id));
      this.notifications = list || [];

      if (!this.notifListInitialized) {
        this.notifListInitialized = true;
        if (this.notifications[0]) {
          this.lastSeenNotifId = this.notifications[0]._id;
        }
        return;
      }

      const newest = this.notifications[0];
      if (!newest) return;

      const isTrulyNew =
        newest._id !== this.lastSeenNotifId &&
        !previousIds.has(newest._id);

      if (isTrulyNew) {
        this.lastSeenNotifId = newest._id;
        this.playNotificationSound();
      }
    });

    this.notificationService.unreadCount$.subscribe(count => {
      this.unreadCount = count || 0;
    });

    this.loadNotifications(1, false);
    this.notificationService.refreshUnreadCount().subscribe({
      next: () => { },
      error: (err) => console.error('Unread count load error', err)
    });
  }

  private loadNotifications(page: number = 1, append: boolean = false) {
    this.notifLoading = true;
    this.notificationService.loadRecent(page, 10, append).subscribe({
      next: (data) => {
        this.notifPage = data.pagination.currentPage;
        this.notifTotalPages = data.pagination.totalPages;
        this.notifLoading = false;
      },
      error: (err) => {
        console.error('Notification load error', err);
        this.notifLoading = false;
      }
    });
  }

  onLoadMoreNotifications() {
    if (this.notifLoading) return;
    if (this.notifPage >= this.notifTotalPages) return;
    const nextPage = this.notifPage + 1;
    this.loadNotifications(nextPage, true);
  }

  onNotificationClick(event: MouseEvent, n: AppNotification) {
    event.preventDefault();
    if (!n.isRead) {
      this.notificationService.markAsRead(n._id).subscribe({
        next: () => {
          this.notificationService.refreshUnreadCount().subscribe({
            next: () => { },
            error: (err) => console.error('Unread refresh error', err)
          });
        },
        error: (err) => console.error('Mark read error', err)
      });
    }
  }

  onMarkAllRead(event: MouseEvent) {
    event.preventDefault();
    if (this.unreadCount === 0) return;

    this.notificationService.markAllAsRead().subscribe({
      next: () => {
        this.unreadCount = 0;
      },
      error: (err) => console.error('Mark all read error', err)
    });
  }

  // =========================
  // Sound helpers
  // =========================
  private initNotificationSound() {
    try {
      this.notifAudio = new Audio('assets/images/notify.mp3');
      this.notifAudio.load();
    } catch (err) {
      console.warn('Notification sound init failed', err);
    }
  }

  private playNotificationSound() {
    if (!this.hasUserInteracted || !this.soundUnlocked) {
      return;
    }

    if (!this.notifAudio) {
      this.initNotificationSound();
    }
    if (!this.notifAudio) return;

    try {
      this.notifAudio.currentTime = 0;
      this.notifAudio.play().catch(err => {
        if (err.name !== 'NotAllowedError') {
          console.warn('Notification sound play error', err);
        }
      });
    } catch (err) {
      console.error('Notification sound error', err);
    }
  }

  @HostListener('document:click', ['$event'])
  onFirstUserInteraction(event: MouseEvent) {
    if (this.hasUserInteracted) return;

    this.hasUserInteracted = true;
    this.unlockNotificationSound();
  }

  private unlockNotificationSound() {
    if (this.soundUnlocked) return;

    if (!this.notifAudio) {
      this.initNotificationSound();
    }

    if (!this.messageAudio) {
      this.initMessageSound();
    }

    const audioToUnlock = this.messageAudio || this.notifAudio;
    if (!audioToUnlock) return;

    try {
      audioToUnlock.muted = true;

      const playPromise = audioToUnlock.play();

      if (playPromise && (playPromise as any).then) {
        (playPromise as Promise<void>)
          .then(() => {
            audioToUnlock.pause();
            audioToUnlock.currentTime = 0;
            audioToUnlock.muted = false;

            if (this.notifAudio) {
              this.notifAudio.muted = false;
              this.notifAudio.currentTime = 0;
            }

            if (this.messageAudio) {
              this.messageAudio.muted = false;
              this.messageAudio.currentTime = 0;
            }

            this.soundUnlocked = true;
            console.log('✅ Notification/message sounds unlocked after first user click');
          })
          .catch(err => {
            console.warn('⚠️ Failed to unlock sounds on first click', err);
          });
      } else {
        audioToUnlock.pause();
        audioToUnlock.currentTime = 0;
        audioToUnlock.muted = false;

        if (this.notifAudio) {
          this.notifAudio.muted = false;
          this.notifAudio.currentTime = 0;
        }

        if (this.messageAudio) {
          this.messageAudio.muted = false;
          this.messageAudio.currentTime = 0;
        }

        this.soundUnlocked = true;
      }
    } catch (err) {
      console.warn('Sound unlock error', err);
    }
  }

  private initMessageSound() {
    try {
      this.messageAudio = new Audio('assets/images/message.mp3');
      this.messageAudio.load();
    } catch (err) {
      console.warn('Message sound init failed', err);
    }
  }

  private playMessageSound() {
    if (!this.hasUserInteracted || !this.soundUnlocked) {
      return;
    }

    if (!this.messageAudio) {
      this.initMessageSound();
    }
    if (!this.messageAudio) return;

    try {
      this.messageAudio.currentTime = 0;
      this.messageAudio.play().catch(err => {
        if (err.name !== 'NotAllowedError') {
          console.warn('Message sound play error', err);
        }
      });
    } catch (err) {
      console.error('Message sound error', err);
    }
  }



  // Others Top bar functions

  toggleMobileMenu(event: any) {
    event.preventDefault();
    this.mobileMenuButtonClicked.emit();
  }

  toggleRightSidebar() {
    this.settingsButtonClicked.emit();
  }

  fullscreen() {
    document.body.classList.toggle('fullscreen-enable');
    if (
      !document.fullscreenElement && !this.element.mozFullScreenElement &&
      !this.element.webkitFullscreenElement
    ) {
      if (this.element.requestFullscreen) {
        this.element.requestFullscreen();
      } else if (this.element.mozRequestFullScreen) {
        this.element.mozRequestFullScreen();
      } else if (this.element.webkitRequestFullscreen) {
        this.element.webkitRequestFullscreen();
      } else if (this.element.msRequestFullscreen) {
        this.element.msRequestFullscreen();
      }
    } else {
      if (this.document.exitFullscreen) {
        this.document.exitFullscreen();
      } else if (this.document.mozCancelFullScreen) {
        this.document.mozCancelFullScreen();
      } else if (this.document.webkitExitFullscreen) {
        this.document.webkitExitFullscreen();
      } else if (this.document.msExitFullscreen) {
        this.document.msExitFullscreen();
      }
    }
  }

  setLanguage(text: string, lang: string, flag: string) {
    this.countryName = text;
    this.flagvalue = flag;
    this.cookieValue = lang;
    this.languageService.setLanguage(lang);
  }

  logout(): void {
    this.authenticationService.logout();
    this.router.navigate(['/account/login']);
  }
}